import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { PaymentMethod, type Product, type ProductWithStock } from "../../../domain/models";
import CashierSellingViewModelState, { OperationMode, Receipt, ReceiptStatus } from "./CashierSellingViewModelState";
import { useAuth } from "../../contexts";
import { useUseCases } from "@/presentation/contexts/DIContext";
import { normalizeBarcodeScanInput, calcCartTotalsFromRoundedTtc, prepareProductSearchFieldValue, shouldAcceptScanAdd, isNumericBarcodeQuery, remainingQuantities } from "@shared/utils";
import {
  listBranchSales,
  listBranchRefunds,
  getSaleByReceiptId,
  type BackendSale,
} from "../../../data/api/salesApiClient";
import { useOfflinePos } from "../../../offline/OfflinePosContext";
import type { OfflineReceiptHistoryEntry } from "@shared/offline";
import { mapSaleReceiptToThermal } from "../../../printing/mapSaleReceiptToThermal";
import { printSaleReceipt } from "../../../printing/receiptPrintService";
import { resolveReceiptPrintErrorMessage } from "../../../printing/receiptPrintErrors";
import {
  sortCategoriesByName,
  sortProductsByCategoryThenName,
  uniqueProductsById,
} from "./freeSaleServices";
import { createFreePriceCartItem } from "./freePriceSale";
import { canSubmitOfflineSale } from "../../../offline/checkoutAuthGate";
import {
  canLoadLocalReceiptHistory,
  canLookupLocalReceipt,
  canPullRemoteReceiptHistory,
} from "../../../offline/receiptHistoryGates";
import { catalogUiLoadKey } from "./catalogUiLoadKey";
import { historyCashierLabel } from "./receiptRefundHelpers";
import { effectiveCashGiven } from "./effectiveCashGiven";

/** USB scanner barcodes are numeric and typically EAN-8 / UPC / EAN-13 / ITF-14. */
function isBarcodeScanQuery(query: string): boolean {
  return isNumericBarcodeQuery(query);
}

function findExactCodeMatch<T extends { id: string; sku?: string; barcode?: string }>(
  products: T[],
  query: string,
): T | undefined {
  const q = query.trim();
  if (!q) return undefined;
  const qLower = q.toLowerCase();
  return products.find(
    (p) =>
      (p.barcode != null && String(p.barcode) === q) ||
      (p.sku != null && p.sku.toLowerCase() === qLower) ||
      p.id === q ||
      p.id.toLowerCase() === qLower,
  );
}

function enrichProductWithStock(
  product: Product | ProductWithStock,
  branchId: string,
  existing?: ProductWithStock,
): ProductWithStock {
  if ("branchStock" in product && product.branchStock) {
    return product as ProductWithStock;
  }
  return {
    ...(product as Product),
    branchStock: existing?.branchStock ?? {
      branchId,
      stock: 0,
      exposedOnSite: 0,
      reservedStock: 0,
      availableStock: 0,
      minimumStock: 10,
    },
  };
}
/*
throw new Error('This ViewModel is deprecated. Use CashierSellingViewModelNew instead.');
*/

// Format current date and time for display
const formatCurrentDateTime = (): string => {
  const now = new Date();
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  };
  return now.toLocaleDateString('en-US', options);
};

// Generate receipt number (RCP-YYYYMMDD-NNNN format per Figma)
const generateReceiptNumber = (): string => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  const seq = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
  return `RCP-${y}${m}${d}-${seq}`;
};

const mapSaleToReceipt = (
  sale: BackendSale,
  status: ReceiptStatus = ReceiptStatus.COMPLETED,
  returnedQuantities?: number[],
): Receipt => ({
  id: sale.visibleId,
  saleVisibleId: sale.visibleId,
  receiptNumber: sale.receiptId,
  items: sale.items,
  subtotal: sale.subtotal,
  discountPercentage: sale.discountPercentage,
  discountAmount: sale.discountAmount,
  grandTotal: sale.totalAmount,
  paymentMethod: sale.payment.method as PaymentMethod,
  cashGiven: sale.payment.cashGiven ?? sale.payment.amountPaid,
  cashReturned: sale.payment.cashReturned ?? 0,
  cashierName: historyCashierLabel(sale),
  createdAt: new Date(sale.createdAt),
  doneAt: new Date(sale.createdAt),
  status,
  returnedQuantities,
});

function classifySaleRefund(
  items: BackendSale['items'],
  related: Array<{ items?: Array<{ productId: string; quantity: number }> }>,
): { status: ReceiptStatus; returnedQuantities?: number[] } {
  if (related.length === 0) return { status: ReceiptStatus.COMPLETED };
  const knowsQuantities = related.every((refund) => Array.isArray(refund.items));
  if (!knowsQuantities) return { status: ReceiptStatus.REFUNDED };
  const left = remainingQuantities(
    items,
    related.map((refund) => ({ items: refund.items ?? [] })),
  );
  const returnedQuantities = items.map((item, index) => item.quantity - (left[index] ?? 0));
  if (left.every((qty) => qty === 0)) {
    return { status: ReceiptStatus.REFUNDED, returnedQuantities };
  }
  if (returnedQuantities.some((qty) => qty > 0)) {
    return { status: ReceiptStatus.PARTIAL, returnedQuantities };
  }
  return { status: ReceiptStatus.COMPLETED };
}

function historyStatus(entry: OfflineReceiptHistoryEntry): ReceiptStatus {
  if (entry.status === 'REFUNDED') return ReceiptStatus.REFUNDED;
  if (entry.status === 'PARTIAL') return ReceiptStatus.PARTIAL;
  return ReceiptStatus.COMPLETED;
}

const mapHistoryEntryToReceipt = (
  entry: OfflineReceiptHistoryEntry,
): Receipt => ({
  id: entry.saleVisibleId,
  saleVisibleId: entry.saleVisibleId,
  receiptNumber: entry.receiptNumber,
  items: entry.items,
  subtotal: entry.subtotal,
  discountPercentage: entry.discountPercentage,
  discountAmount: entry.discountAmount,
  grandTotal: entry.grandTotal,
  paymentMethod: entry.paymentMethod as PaymentMethod,
  cashGiven: entry.cashGiven,
  cashReturned: entry.cashReturned,
  cashierName: historyCashierLabel(entry),
  createdAt: new Date(entry.createdAt),
  doneAt: new Date(entry.createdAt),
  status: historyStatus(entry),
  returnedQuantities: entry.items.map(
    (item) => (item as { returnedQuantity?: number }).returnedQuantity ?? 0,
  ),
});

const initialState: CashierSellingViewModelState = {
  receiptNumber: generateReceiptNumber(),
  cashierName: "Cashier Name",
  currentDateTime: formatCurrentDateTime(),
  operationMode: OperationMode.CART,
  cart: [],
  searchTerm: "",
  quantity: "1",
  editingCartIndex: null,
  products: [],
  specialSaleProducts: [],
  freePriceCategories: [],
  categories: [],
  selectedCategoryId: null,
  isLoadingProducts: false,
  isLoadingCategories: false,
  subtotal: 0,
  discountPercentage: 0,
  discountAmount: 0,
  taxAmount: 0,
  grandTotal: 0,
  paymentMethod: PaymentMethod.CASH,
  cashGiven: 0,
  cashToReturn: 0,
  isProcessingPayment: false,
  showPaymentModal: false,
  selectedReceipt: null,
  receiptHistory: [],
  currentReceipt: null,
  cashKeypadValue: "",
  isEditingCashGiven: false,
  showCashKeypad: false,
  shouldPrintReceipt: false,
  showSearchResults: false,
  notification: {
    message: "",
    type: 'info',
    show: false
  },
  errors: {
    quantity: "",
    payment: "",
    general: ""
  }
};

export const useCashierSellingViewModel = () => {
  const { userSession, logout } = useAuth();
  const { t } = useTranslation();
  const offlinePos = useOfflinePos();
  const {
    getProductsAvailableInBranchUseCase,
    getProductCategoriesUseCase,
    searchProductsUseCase,
    getProductsByCategoryUseCase,
  } = useUseCases();

  const [state, setState] = useState<CashierSellingViewModelState>({
    ...initialState,
    cashierName: userSession?.user.firstName && userSession?.user.lastName
      ? `${userSession.user.firstName} ${userSession.user.lastName}`
      : userSession?.user.username || "Cashier Name"
  });

  useEffect(() => {
    const u = userSession?.user;
    if (!u) return;
    const nextName =
      u.firstName || u.lastName
        ? `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim()
        : u.username || 'Cashier Name';
    setState((prev) =>
      prev.cashierName === nextName ? prev : { ...prev, cashierName: nextName },
    );
  }, [userSession?.user.firstName, userSession?.user.lastName, userSession?.user.username]);

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Tracks last search keystroke so a new scanner burst replaces a completed barcode. */
  const searchInputMetaRef = useRef({ at: 0, value: '', keyAt: [] as number[] });
  /** Prevents debounce+Enter (or double terminator) from adding the same scan twice. */
  const lastScanAddRef = useRef({ code: '', at: 0 });
  const SEARCH_SCAN_IDLE_MS = 280;
  const BARCODE_SCAN_DEBOUNCE_MS = 120;
  const NAME_SEARCH_DEBOUNCE_MS = 300;
  const stateRef = useRef(state);
  stateRef.current = state;
  const notificationTokenRef = useRef(0);

  const flashNotification = useCallback(
    (
      message: string,
      type: 'success' | 'error' | 'info' = 'success',
      durationMs = 3000,
    ) => {
      const token = ++notificationTokenRef.current;
      setState((prev) => ({
        ...prev,
        notification: { message, type, show: true },
      }));
      setTimeout(() => {
        setState((prev) => {
          if (notificationTokenRef.current !== token) return prev;
          return {
            ...prev,
            notification: { ...prev.notification, show: false },
          };
        });
      }, durationMs);
    },
    [],
  );

  // Cart totals from rounded shelf TTC (qty × PU TTC), not HT × (1+TVA).
  useEffect(() => {
    const totals = calcCartTotalsFromRoundedTtc(state.cart, state.discountPercentage);
    const cashToReturn = Math.max(0, state.cashGiven - totals.grandTotal);

    setState((prev) => {
      if (
        prev.subtotal === totals.subtotalTtc &&
        prev.discountAmount === totals.discountAmountTtc &&
        prev.taxAmount === totals.taxAmount &&
        prev.grandTotal === totals.grandTotal &&
        prev.cashToReturn === cashToReturn
      ) {
        return prev;
      }
      return {
        ...prev,
        subtotal: totals.subtotalTtc,
        discountAmount: totals.discountAmountTtc,
        taxAmount: totals.taxAmount,
        grandTotal: totals.grandTotal,
        cashToReturn,
      };
    });
  }, [state.cart, state.discountPercentage, state.cashGiven]);

  // Update current time every second for real-time display
  useEffect(() => {
    const interval = setInterval(() => {
      setState(prev => ({
        ...prev,
        currentDateTime: formatCurrentDateTime()
      }));
    }, 1000); // Update every second

    return () => clearInterval(interval);
  }, []);

  const fetchAllBranchProducts = useCallback(async (branchId: string) => {
    if (offlinePos.usesLocalCatalog) {
      try {
        offlinePos.ensureCatalogOrThrow();
      } catch {
        return [];
      }
      return offlinePos.getLocalBranchProducts(branchId, 1, 500).products;
    }
    const result = await getProductsAvailableInBranchUseCase.execute(branchId, 1, 100);
    return result.products;
  }, [getProductsAvailableInBranchUseCase, offlinePos]);

  const onCategorySelected = useCallback(async (categoryId: string) => {
    setState(prev => ({
      ...prev,
      selectedCategoryId: categoryId || null,
      searchTerm: ""
    }));

    try {
      setState(prev => ({ ...prev, isLoadingProducts: true }));
      const branchId = userSession?.branchId || 'branch_qalam_benghazi';

      if (!categoryId) {
        const products = await fetchAllBranchProducts(branchId);
        setState((prev) => ({ ...prev, products, isLoadingProducts: false }));
        return;
      }

      const result = offlinePos.usesLocalCatalog
        ? offlinePos.getLocalProductsByCategory(categoryId, branchId)
        : await getProductsByCategoryUseCase.execute(categoryId, branchId);

      setState(prev => ({
        ...prev,
        products: result.products,
        isLoadingProducts: false
      }));

      console.log(`✅ Loaded ${result.products.length} products for category: ${categoryId}`);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load category products';
      setState(prev => ({
        ...prev,
        isLoadingProducts: false,
        errors: { ...prev.errors, products: errorMessage }
      }));
      console.error('Failed to load category products:', error);
    }
  }, [getProductsByCategoryUseCase, fetchAllBranchProducts, userSession?.branchId, offlinePos])

  const onLoadAllProducts = useCallback(async () => {
    const branchId = userSession?.branchId || 'branch_qalam_benghazi';
    try {
      setState((prev) => ({ ...prev, isLoadingProducts: true }));
      const products = await fetchAllBranchProducts(branchId);
      setState((prev) => ({ ...prev, products, isLoadingProducts: false }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        isLoadingProducts: false,
        notification: {
          message: error instanceof Error ? error.message : 'Échec du chargement du stock',
          type: 'error',
          show: true,
        },
      }));
    }
  }, [fetchAllBranchProducts, userSession?.branchId]);

  // Load categories with products available in the current branch
  const onLoadCategories = useCallback(async () => {
    try {
      setState(prev => ({
        ...prev,
        isLoadingCategories: true,
        errors: { ...prev.errors, categories: "" }
      }));

      const branchId = userSession?.branchId || 'branch_qalam_benghazi';
      const categories = offlinePos.usesLocalCatalog
        ? offlinePos.getLocalCategories(branchId)
        : await getProductCategoriesUseCase.execute(branchId);
      const specialCategories = categories.filter(
        (category) => category.isFreeSaleOrService,
      );
      const freePriceCategories = sortCategoriesByName(
        categories.filter(
          (category) => category.isFreePrice && category.isActive,
        ),
      );
      const specialProductsByCategory = await Promise.all(
        specialCategories.map(async (category) => {
          if (offlinePos.usesLocalCatalog) {
            const products: ProductWithStock[] = [];
            let page = 1;
            let hasMore = true;
            while (hasMore) {
              const result = offlinePos.getLocalProductsByCategory(
                category.id,
                branchId,
                page,
                100,
              );
              products.push(...result.products);
              hasMore = result.hasMore;
              page += 1;
            }
            return products;
          }
          const products: ProductWithStock[] = [];
          let page = 1;
          let hasMore = true;
          while (hasMore) {
            const result = await getProductsByCategoryUseCase.execute(
              category.id,
              branchId,
              page,
              100,
            );
            products.push(...result.products);
            hasMore = result.hasMore;
            page += 1;
          }
          return products;
        }),
      );
      const specialSaleProducts = sortProductsByCategoryThenName(
        uniqueProductsById(specialProductsByCategory),
        categories,
      );

      setState(prev => ({
        ...prev,
        categories,
        specialSaleProducts,
        freePriceCategories,
        isLoadingCategories: false
      }));

      // "Tous" is the only all-products filter and is selected initially.
      await onCategorySelected("");

      console.log(
        `✅ Loaded ${categories.length} categories and ${specialSaleProducts.length} Vente libre & Services products in branch ${branchId}`,
      );
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load categories';
      setState(prev => ({
        ...prev,
        isLoadingCategories: false,
        errors: { ...prev.errors, categories: errorMessage }
      }));
      console.error('Failed to load categories:', error);
    }
  }, [
    getProductCategoriesUseCase,
    getProductsByCategoryUseCase,
    userSession?.branchId,
    onCategorySelected,
    offlinePos,
  ])

  const loadReceiptHistory = useCallback(async () => {
    const token = userSession?.token.accessToken ?? '';
    const branchId = userSession?.branchId || 'branch_qalam_benghazi';
    if (
      !canLoadLocalReceiptHistory({
        accessToken: token,
        usesLocalCatalog: offlinePos.usesLocalCatalog,
      })
    ) {
      return;
    }
    if (offlinePos.usesLocalCatalog) {
      if (
        canPullRemoteReceiptHistory({
          accessToken: token,
          usesLocalCatalog: offlinePos.usesLocalCatalog,
          isOnline: offlinePos.isOnline,
        })
      ) {
        try {
          await offlinePos.pullReceiptHistory(token, branchId);
        } catch (error) {
          // Soft-fail: backend may be briefly down; local SQLite history still usable.
          console.warn('Receipt history pull failed; using local cache', error);
        }
      }
      const local = offlinePos.getLocalReceiptHistory(branchId);
      setState((prev) => ({
        ...prev,
        receiptHistory: local.map((e) => mapHistoryEntryToReceipt(e)),
      }));
      return;
    }

    try {
      const [sales, refunds] = await Promise.all([
        listBranchSales(token, branchId),
        listBranchRefunds(token, branchId).catch(() => []),
      ]);

      const receipts = sales.map((sale) => {
        const related = refunds.filter(
          (refund) =>
            refund.originalReceiptId === sale.receiptId ||
            refund.originalSaleId === sale.visibleId ||
            refund.originalSaleId === sale.id,
        );
        const classified = classifySaleRefund(sale.items, related);
        return mapSaleToReceipt(sale, classified.status, classified.returnedQuantities);
      });
      setState((prev) => ({ ...prev, receiptHistory: receipts }));
    } catch (error) {
      console.error('Failed to load sales for refund:', error);
      setState((prev) => ({
        ...prev,
        notification: {
          message:
            error instanceof Error
              ? error.message
              : 'Failed to load receipts for refund',
          type: 'error',
          show: true,
        },
      }));
    }
  }, [
    userSession?.token.accessToken,
    userSession?.branchId,
    offlinePos,
  ]);

  const onModeToggle = useCallback(async () => {
    const nextMode =
      state.operationMode === OperationMode.CART
        ? OperationMode.REFUND
        : OperationMode.CART;

    setState((prev) => ({
      ...prev,
      operationMode: nextMode,
      selectedReceipt: nextMode === OperationMode.CART ? null : prev.selectedReceipt,
      cart: nextMode === OperationMode.CART ? [] : prev.cart,
      errors: { ...prev.errors, payment: '', general: '' },
    }));

    if (nextMode === OperationMode.REFUND) {
      await loadReceiptHistory();
    }
  }, [state.operationMode, loadReceiptHistory]);

  const onEnterRefundMode = useCallback(async () => {
    setState((prev) => ({
      ...prev,
      operationMode: OperationMode.REFUND,
      selectedReceipt: null,
      cart: [],
      discountPercentage: 0,
      errors: { ...prev.errors, payment: '', general: '' },
    }));
    await loadReceiptHistory();
  }, [loadReceiptHistory]);

  /** Append / increment a product line; keeps the catalog grid untouched. */
  const appendProductToCart = useCallback((
    product: Product | ProductWithStock,
    options?: { clearSearch?: boolean },
  ) => {
    const branchId = userSession?.branchId || 'branch_qalam_benghazi';
    const clearSearch = options?.clearSearch === true;
    setState((prev) => {
      const enriched = enrichProductWithStock(
        product,
        branchId,
        prev.products.find((x) => x.id === product.id),
      );
      const quantity = 1;
      const availableStock =
        enriched.branchStock?.availableStock ?? enriched.branchStock?.stock ?? 0;
      const newCart = [...prev.cart];
      const existingItemIndex = newCart.findIndex((item) => item.productId === enriched.id);
      const subtotal = quantity * enriched.salePriceHT;

      let currentReceipt = prev.currentReceipt;
      let receiptNumber = prev.receiptNumber;

      if (newCart.length === 0 && !currentReceipt) {
        currentReceipt = {
          id: `receipt-${Date.now()}`,
          receiptNumber: generateReceiptNumber(),
          items: [],
          subtotal: 0,
          discountPercentage: 0,
          discountAmount: 0,
          grandTotal: 0,
          paymentMethod: PaymentMethod.CASH,
          cashGiven: 0,
          cashReturned: 0,
          cashierName: prev.cashierName,
          createdAt: new Date(),
          doneAt: null,
          status: ReceiptStatus.ONGOING,
        };
        receiptNumber = currentReceipt.receiptNumber;
      }

      if (existingItemIndex >= 0) {
        const existingItem = newCart[existingItemIndex];
        const newQuantity = existingItem.quantity + quantity;
        const originalSubtotal = newQuantity * existingItem.unitPrice;
        const discountAmount = (originalSubtotal * existingItem.productDiscountPercentage) / 100;
        newCart[existingItemIndex] = {
          ...existingItem,
          quantity: newQuantity,
          productDiscountAmount: discountAmount,
          subtotal: originalSubtotal,
          availableStock,
        };
      } else {
        newCart.push({
          productId: enriched.id,
          productName: enriched.name,
          unitPrice: enriched.salePriceHT,
          taxRate: enriched.taxRate,
          quantity,
          subtotal,
          productDiscountPercentage: 0,
          productDiscountAmount: 0,
          availableStock,
        });
      }

      return {
        ...prev,
        cart: newCart,
        currentReceipt,
        receiptNumber,
        quantity: '1',
        isLoadingProducts: false,
        errors: { ...prev.errors, general: '', quantity: '' },
        ...(clearSearch
          ? { searchTerm: '', showSearchResults: false }
          : {}),
      };
    });
    if (clearSearch) {
      searchInputMetaRef.current = { at: Date.now(), value: '', keyAt: [] };
    }
  }, [userSession?.branchId]);

  const notifyScanMiss = useCallback((code: string) => {
    setState((prev) => ({
      ...prev,
      searchTerm: '',
      showSearchResults: false,
      isLoadingProducts: false,
      notification: {
        message: `Produit introuvable — ${code}`,
        type: 'error',
        show: true,
      },
    }));
    searchInputMetaRef.current = { at: Date.now(), value: '', keyAt: [] };
    window.setTimeout(() => {
      setState((prev) => ({
        ...prev,
        notification: { ...prev.notification, show: false },
      }));
    }, 3000);
  }, []);

  /**
   * Exact barcode/SKU hit → cart. Catalog grid is not replaced with search results.
   * Returns true when a line was added.
   */
  const resolveBarcodeScanToCart = useCallback(async (rawCode: string, branchId: string): Promise<boolean> => {
    const code = normalizeBarcodeScanInput(rawCode).trim();
    if (!code) return false;

    const now = Date.now();
    if (
      !shouldAcceptScanAdd(
        code,
        lastScanAddRef.current.code,
        lastScanAddRef.current.at,
        now,
      )
    ) {
      return false;
    }

    const localHit = findExactCodeMatch(stateRef.current.products, code);
    if (localHit) {
      lastScanAddRef.current = { code, at: now };
      appendProductToCart(localHit, { clearSearch: true });
      return true;
    }

    if (offlinePos.usesLocalCatalog) {
      try {
        offlinePos.ensureCatalogOrThrow();
        const found = offlinePos.searchLocalProducts(code, branchId);
        const exact = findExactCodeMatch(found, code);
        if (exact) {
          lastScanAddRef.current = { code, at: now };
          appendProductToCart(exact, { clearSearch: true });
          return true;
        }
      } catch {
        notifyScanMiss(code);
        return false;
      }
    }

    try {
      const found = await searchProductsUseCase.execute(code, branchId);
      const exact = findExactCodeMatch(found, code);
      if (exact) {
        lastScanAddRef.current = { code, at: now };
        appendProductToCart(exact, { clearSearch: true });
        return true;
      }
      notifyScanMiss(code);
      return false;
    } catch (error) {
      setState((prev) => ({
        ...prev,
        isLoadingProducts: false,
        notification: {
          message: error instanceof Error ? error.message : 'Recherche échouée',
          type: 'error',
          show: true,
        },
      }));
      return false;
    }
  }, [appendProductToCart, notifyScanMiss, searchProductsUseCase, offlinePos]);

  /** Enter / scanner terminator: try exact code add (barcode or SKU). */
  const onBarcodeScanSubmit = useCallback(async () => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    const branchId = userSession?.branchId || 'branch_qalam_benghazi';
    const code = normalizeBarcodeScanInput(searchInputMetaRef.current.value || stateRef.current.searchTerm).trim();
    if (!code) return;

    // Prefer scan-to-cart for barcode-like and SKU-like codes (no spaces).
    const looksLikeCode = isBarcodeScanQuery(code) || /^[A-Za-z0-9\-_.]{3,32}$/.test(code);
    if (!looksLikeCode) return;

    await resolveBarcodeScanToCart(code, branchId);
  }, [resolveBarcodeScanToCart, userSession?.branchId]);

  const onProductSearch = useCallback((searchTerm: string) => {
    const now = Date.now();
    const meta = searchInputMetaRef.current;
    const prepared = prepareProductSearchFieldValue(
      meta.value,
      searchTerm,
      { keyAt: meta.keyAt, lastInputAt: meta.at },
      now,
      SEARCH_SCAN_IDLE_MS,
    );
    const normalizedSearchTerm = prepared.value;
    const inputMode = prepared.mode;
    searchInputMetaRef.current = {
      at: now,
      value: normalizedSearchTerm,
      keyAt: prepared.keyAt,
    };

    setState((prev) => ({
      ...prev,
      searchTerm: normalizedSearchTerm,
      showSearchResults: normalizedSearchTerm.length > 0,
    }));

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    const branchId = userSession?.branchId || 'branch_qalam_benghazi';
    const trimmed = normalizedSearchTerm.trim();

    if (trimmed.length < 2) {
      if (!trimmed) {
        void onCategorySelected(stateRef.current.selectedCategoryId || '');
      }
      return;
    }

    // Fast barcode scan: resolve exact code → cart (deduped vs Enter).
    // Slow typing of digits still goes through live search (name/sku/barcode/description).
    if (inputMode === 'scan' && isBarcodeScanQuery(trimmed)) {
      searchDebounceRef.current = setTimeout(() => {
        void resolveBarcodeScanToCart(trimmed, branchId);
      }, BARCODE_SCAN_DEBOUNCE_MS);
      return;
    }

    // Typed query: live filter on name / description / barcode / sku.
    searchDebounceRef.current = setTimeout(async () => {
      try {
        setState((prev) => ({ ...prev, isLoadingProducts: true, selectedCategoryId: null }));
        const found = offlinePos.usesLocalCatalog
          ? offlinePos.searchLocalProducts(trimmed, branchId)
          : await searchProductsUseCase.execute(trimmed, branchId);
        setState((prev) => {
          const enriched = found.map((p) =>
            enrichProductWithStock(
              p,
              branchId,
              prev.products.find((x) => x.id === p.id),
            ),
          );
          return { ...prev, products: enriched, isLoadingProducts: false };
        });
      } catch (error) {
        setState((prev) => ({
          ...prev,
          isLoadingProducts: false,
          notification: {
            message: error instanceof Error ? error.message : 'Recherche échouée',
            type: 'error',
            show: true,
          },
        }));
      }
    }, NAME_SEARCH_DEBOUNCE_MS);
  }, [searchProductsUseCase, userSession?.branchId, onCategorySelected, resolveBarcodeScanToCart, offlinePos]);

  const onLookupReceipt = useCallback(async (receiptId: string): Promise<Receipt | null> => {
    const token = userSession?.token.accessToken ?? '';
    const branchId = userSession?.branchId || 'branch_qalam_benghazi';
    const lookupGate = canLookupLocalReceipt({
      accessToken: token,
      usesLocalCatalog: offlinePos.usesLocalCatalog,
      receiptId,
    });
    if (!lookupGate.ok) {
      setState((prev) => ({
        ...prev,
        errors: {
          ...prev.errors,
          general:
            lookupGate.reason === 'needs_token'
              ? 'Connexion requise pour rechercher un reçu'
              : 'Saisissez un numéro de reçu',
        },
      }));
      return null;
    }

    const trimmed = receiptId.trim();
    const upper = trimmed.toUpperCase();

    if (offlinePos.usesLocalCatalog) {
      const localHit = offlinePos
        .getLocalReceiptHistory(branchId)
        .find(
          (e) =>
            e.receiptNumber === trimmed ||
            e.receiptNumber === upper ||
            e.id === trimmed ||
            e.saleVisibleId === trimmed,
        );
      if (localHit) {
        const receipt = mapHistoryEntryToReceipt(localHit);
        if (receipt.status === ReceiptStatus.REFUNDED) {
          setState((prev) => ({
            ...prev,
            errors: {
              ...prev.errors,
              general: `Le reçu ${receipt.receiptNumber} a déjà été remboursé`,
            },
          }));
          return null;
        }
        setState((prev) => ({
          ...prev,
          selectedReceipt: receipt,
          cart: [...receipt.items],
          discountPercentage: receipt.discountPercentage,
          operationMode: OperationMode.REFUND,
          errors: { ...prev.errors, general: '', payment: '' },
        }));
        return receipt;
      }
      if (!offlinePos.isOnline) {
        setState((prev) => ({
          ...prev,
          errors: { ...prev.errors, general: 'Reçu introuvable' },
        }));
        return null;
      }
      if (!token) {
        setState((prev) => ({
          ...prev,
          errors: { ...prev.errors, general: 'Reçu introuvable hors ligne' },
        }));
        return null;
      }
    }

    if (!token) {
      setState((prev) => ({
        ...prev,
        errors: {
          ...prev.errors,
          general: 'Connexion requise pour rechercher un reçu',
        },
      }));
      return null;
    }

    try {
      const sale = await getSaleByReceiptId(token, trimmed);
      const known = state.receiptHistory.find(
        (r) =>
          r.receiptNumber === sale.receiptId ||
          r.id === sale.visibleId ||
          r.saleVisibleId === sale.visibleId,
      );
      if (known?.status === ReceiptStatus.REFUNDED) {
        setState((prev) => ({
          ...prev,
          errors: {
            ...prev.errors,
            general: `Le reçu ${sale.receiptId} a déjà été remboursé`,
          },
        }));
        return null;
      }
      const receipt = mapSaleToReceipt(
        sale,
        known?.status ?? ReceiptStatus.COMPLETED,
        known?.returnedQuantities,
      );
      setState((prev) => ({
        ...prev,
        selectedReceipt: receipt,
        cart: [...receipt.items],
        discountPercentage: receipt.discountPercentage,
        operationMode: OperationMode.REFUND,
        errors: { ...prev.errors, general: '', payment: '' },
      }));
      return receipt;
    } catch (error) {
      setState((prev) => ({
        ...prev,
        errors: {
          ...prev.errors,
          general: error instanceof Error ? error.message : 'Reçu introuvable',
        },
      }));
      return null;
    }
  }, [
    userSession?.token.accessToken,
    userSession?.branchId,
    state.receiptHistory,
    offlinePos,
  ]);

  const onProductAddToCart = useCallback((productId: string) => {
    const product = [
      ...stateRef.current.specialSaleProducts,
      ...stateRef.current.products,
    ].find((p) => p.id === productId);
    if (!product) {
      setState((prev) => ({
        ...prev,
        notification: {
          message: 'Product not found',
          type: 'error',
          show: true,
        },
      }));
      setTimeout(() => {
        setState((prev) => ({
          ...prev,
          notification: { ...prev.notification, show: false },
        }));
      }, 3000);
      return;
    }

    appendProductToCart(product);
  }, [appendProductToCart]);

  const onFreePriceProductAddToCart = useCallback(
    (categoryId: string, designation: string, priceTtc: number) => {
      try {
        const item = createFreePriceCartItem({
          categoryId,
          designation,
          priceTtc,
        });
        setState((prev) => ({
          ...prev,
          cart: [...prev.cart, item],
        }));
        flashNotification(`${item.productName} ajouté au panier`, 'success');
      } catch (error) {
        flashNotification(
          error instanceof Error ? error.message : 'Prix libre invalide',
          'error',
        );
      }
    },
    [flashNotification],
  );

  const onEditCartItem = useCallback((index: number) => {
    setState(prev => ({
      ...prev,
      editingCartIndex: index,
      quantity: prev.cart[index].quantity.toString()
    }));
  }, [])

  const onUpdateCartQuantity = useCallback((index: number, newQuantity: number) => {
    setState(prev => {
      const newCart = [...prev.cart];
      const cartItem = newCart[index];
      if (!cartItem) return prev;

      const product = [...prev.specialSaleProducts, ...prev.products].find(
        (p) => p.id === cartItem.productId,
      );
      const availableStock =
        product?.branchStock?.availableStock ??
        product?.branchStock?.stock ??
        cartItem.availableStock;

      const quantity = Math.max(1, parseInt(newQuantity.toString()));

      // Recalculate discount amount and subtotal when quantity changes
      const originalSubtotal = quantity * cartItem.unitPrice;
      const discountAmount = (originalSubtotal * cartItem.productDiscountPercentage) / 100;

      newCart[index] = {
        ...newCart[index],
        quantity: quantity,
        productDiscountAmount: discountAmount,
        subtotal: originalSubtotal, // Subtotal is quantity * unitPrice (no discounts)
        ...(availableStock !== undefined ? { availableStock } : {}),
      };

      return {
        ...prev,
        cart: newCart,
        editingCartIndex: null,
        quantity: "1"
      };
    });
  }, [])

  const onUpdateCartItemDiscount = useCallback((index: number, discountPercentage: number) => {
    setState(prev => {
      const newCart = [...prev.cart];
      const cartItem = newCart[index];

      // Calculate discount based on original item subtotal (quantity * unit price)
      const originalSubtotal = cartItem.quantity * cartItem.unitPrice;
      const discountAmount = (originalSubtotal * discountPercentage) / 100;

      newCart[index] = {
        ...newCart[index],
        productDiscountPercentage: discountPercentage,
        productDiscountAmount: discountAmount,
        subtotal: originalSubtotal // Subtotal is quantity * unitPrice (no discounts)
      };

      return {
        ...prev,
        cart: newCart
      };
    });
  }, [])

  const onCancelCartEdit = useCallback(() => {
    setState(prev => ({
      ...prev,
      editingCartIndex: -1,
      quantity: "1"
    }));
  }, [])

  const onQuantityKeypad = useCallback((digit: string) => {
    setState(prev => {
      // If it's a direct value (from input change), replace the quantity
      if (/^\d+$/.test(digit)) {
        const newQuantity = parseInt(digit);
        if (newQuantity <= 999) {
          return { ...prev, quantity: digit };
        }
        return prev;
      }

      // Handle keypad button presses
      if (digit === "clear") {
        return { ...prev, quantity: "" };
      }

      if (digit === "backspace") {
        return { ...prev, quantity: prev.quantity.slice(0, -1) };
      }

      // Single digit append
      const newQuantity = prev.quantity + digit;
      if (parseInt(newQuantity || "0") <= 999) {
        return { ...prev, quantity: newQuantity };
      }

      return prev;
    });
  }, [])

  const onRemoveFromCart = useCallback((index: number) => {
    setState(prev => ({
      ...prev,
      cart: prev.cart.filter((_, i) => i !== index)
    }));
  }, [])

  const onClearCart = useCallback(() => {
    setState(prev => {
      // Save cancelled receipt if there was one in progress
      let updatedReceiptHistory = prev.receiptHistory;
      if (prev.currentReceipt && prev.cart.length > 0) {
        const cancelledReceipt: Receipt = {
          ...prev.currentReceipt,
          items: [...prev.cart],
          subtotal: prev.subtotal,
          discountPercentage: prev.discountPercentage,
          discountAmount: prev.discountAmount,
          grandTotal: prev.grandTotal,
          doneAt: new Date(),
          status: ReceiptStatus.CANCELLED
        };
        updatedReceiptHistory = [...prev.receiptHistory, cancelledReceipt];
      }

      return {
        ...prev,
        cart: [],
        quantity: "1",
        discountPercentage: 0,
        currentReceipt: null,
        receiptNumber: generateReceiptNumber(),
        receiptHistory: updatedReceiptHistory
      };
    });
  }, [])

  const onDiscountChanged = useCallback((percentage: number) => {
    setState(prev => ({
      ...prev,
      discountPercentage: Math.max(0, Math.min(100, percentage))
    }));
  }, [])

  const onPaymentMethodSelected = useCallback((method: PaymentMethod) => {
    setState(prev => ({
      ...prev,
      paymentMethod: method,
      // Card auto-fills exact total; cash/cheque keep any amount already entered
      // (quick-amount buttons set cash then select CASH — must not wipe).
      cashGiven: method === PaymentMethod.CARD ? prev.grandTotal : prev.cashGiven,
    }));
  }, [])

  const onCashGivenChanged = useCallback((amount: number) => {
    const safe = Math.max(0, amount);
    setState(prev => ({
      ...prev,
      cashGiven: safe,
      cashKeypadValue: safe === 0 ? '' : String(safe),
    }));
  }, [])

  const onConfirmPurchase = useCallback(async () => {
    if (state.cart.length === 0) {
      setState((prev) => ({
        ...prev,
        errors: { ...prev.errors, payment: 'Cart is empty' },
      }));
      return;
    }

    if (
      state.paymentMethod === PaymentMethod.CARD ||
      state.paymentMethod === PaymentMethod.CHEQUE
    ) {
      setState((prev) => ({
        ...prev,
        errors: {
          ...prev.errors,
          payment: 'Règlement carte/chèque — bientôt disponible (espèces uniquement)',
        },
      }));
      return;
    }

    const paidCash = effectiveCashGiven(
      state.cashKeypadValue,
      state.cashGiven,
      state.grandTotal,
    );
    const cashReturned = Math.max(0, paidCash - state.grandTotal);

    if (
      state.paymentMethod === PaymentMethod.CASH &&
      paidCash < state.grandTotal
    ) {
      setState((prev) => ({
        ...prev,
        errors: { ...prev.errors, payment: 'Insufficient cash provided' },
      }));
      return;
    }

    const token = userSession?.token.accessToken ?? '';
    const branchId = userSession?.branchId || 'branch_qalam_benghazi';
    const cashierId = userSession?.user.id || '';
    const allowOfflineCheckout = canSubmitOfflineSale({
      accessToken: token,
      usesLocalCatalog: offlinePos.usesLocalCatalog,
      sessionMode: userSession?.sessionMode,
      isOnline: offlinePos.isOnline,
    });
    if (!allowOfflineCheckout) {
      setState((prev) => ({
        ...prev,
        errors: { ...prev.errors, payment: 'Not authenticated' },
      }));
      return;
    }

    setState((prev) => ({ ...prev, isProcessingPayment: true }));

    try {
      const cartId = `cart_${Date.now()}`;
      const saleResult = await offlinePos.submitSale(
        token,
        {
          cartId,
          cashierId,
          branchId,
          items: state.cart,
          discountPercentage: state.discountPercentage,
          subtotal: state.subtotal,
          discountAmount: state.discountAmount,
          totalAmount: state.grandTotal,
          payment: {
            method: state.paymentMethod,
            amountPaid: state.grandTotal,
            cashGiven: paidCash,
            cashReturned,
          },
          shouldPrintReceipt: state.shouldPrintReceipt,
        },
        { cashierName: state.cashierName },
      );

      const receiptId = saleResult.receiptId;
      const completedReceipt: Receipt = {
        id: receiptId,
        saleVisibleId: receiptId,
        receiptNumber: receiptId,
        items: state.cart,
        subtotal: state.subtotal,
        discountPercentage: state.discountPercentage,
        discountAmount: state.discountAmount,
        grandTotal: state.grandTotal,
        paymentMethod: state.paymentMethod,
        cashGiven: paidCash,
        cashReturned,
        cashierName: state.cashierName,
        createdAt: new Date(),
        doneAt: new Date(),
        status: ReceiptStatus.COMPLETED,
      };

      const offlineNote =
        saleResult.mode === 'offline'
          ? ' (hors ligne — synchronisation en attente)'
          : '';

      setState((prev) => ({
        ...prev,
        isProcessingPayment: false,
        cart: [],
        discountPercentage: 0,
        cashGiven: 0,
        cashKeypadValue: '',
        quantity: '1',
        receiptNumber: generateReceiptNumber(),
        currentReceipt: null,
        receiptHistory: [...prev.receiptHistory, completedReceipt],
        errors: { quantity: '', payment: '', general: '' },
      }));
      flashNotification(`Sale completed (${receiptId})${offlineNote}`, 'success');

      if (state.shouldPrintReceipt) {
        const branchName =
          userSession?.branchName ?? userSession?.branchId ?? '';
        const thermal = mapSaleReceiptToThermal(completedReceipt, {
          branchName,
          taxAmount: state.taxAmount,
        });
        void printSaleReceipt(thermal, userSession?.token.accessToken ?? '').then((result) => {
          if (result.ok) return;
          flashNotification(
            resolveReceiptPrintErrorMessage(result.code, result.error, t),
            'error',
            5000,
          );
        });
      }

      // Refresh the selected product view so zero/negative stock is visible
      // immediately after the sale.
      void onCategorySelected(state.selectedCategoryId ?? "");
    } catch (error) {
      setState((prev) => ({
        ...prev,
        isProcessingPayment: false,
        errors: {
          ...prev.errors,
          payment:
            error instanceof Error ? error.message : 'Payment failed',
        },
      }));
    }
  }, [
    state.cart,
    state.paymentMethod,
    state.cashGiven,
    state.cashKeypadValue,
    state.grandTotal,
    state.cashToReturn,
    state.discountPercentage,
    state.subtotal,
    state.discountAmount,
    state.shouldPrintReceipt,
    state.taxAmount,
    userSession?.branchName,
    userSession?.branchId,
    state.cashierName,
    state.selectedCategoryId,
    onCategorySelected,
    userSession,
    t,
    offlinePos,
    flashNotification,
  ]);

  const onCancelPurchase = useCallback(() => {
    setState(prev => ({
      ...prev,
      showPaymentModal: false,
      errors: { ...prev.errors, payment: "" }
    }));
  }, [])

  const onLogout = useCallback(() => {
    logout();
  }, [logout])

  const onReceiptPrint = useCallback(async (receipt: Receipt) => {
    const branchName =
      userSession?.branchName ?? userSession?.branchId ?? '';
    const thermal = mapSaleReceiptToThermal(receipt, { branchName });
    const result = await printSaleReceipt(thermal, userSession?.token.accessToken ?? '');
    if (result.ok) return;
    setState((prev) => ({
      ...prev,
      notification: {
        message: resolveReceiptPrintErrorMessage(result.code, result.error, t),
        type: 'error',
        show: true,
      },
    }));
    setTimeout(() => {
      setState((prev) => ({
        ...prev,
        notification: { ...prev.notification, show: false },
      }));
    }, 5000);
  }, [userSession?.branchName, userSession?.branchId, userSession?.token.accessToken, t]);

  const onReceiptSelected = useCallback((receipt: Receipt) => {
    setState(prev => ({
      ...prev,
      selectedReceipt: receipt,
      cart: [...receipt.items], // Load receipt items into cart for refund
      discountPercentage: receipt.discountPercentage,
      operationMode: OperationMode.REFUND,
    }));
  }, [])

  const onClearRefund = useCallback(() => {
    setState(prev => ({
      ...prev,
      selectedReceipt: null,
      cart: [],
      discountPercentage: 0,
      operationMode: OperationMode.CART,
      errors: { ...prev.errors, payment: '', general: '' },
    }));
  }, [])

  const onProcessRefund = useCallback(async (opts?: {
    reason?: string;
    receipt?: Receipt;
    items?: Receipt['items'];
    refundAmount?: number;
    collectedAmount?: number;
    exchanges?: Array<{
      originalProductId: string;
      replacementProductId: string;
      replacementName: string;
      quantity: number;
      unitPrice: number;
      taxRate: number;
    }>;
    settled?: boolean;
  }): Promise<{ refundRef: string } | null> => {
    const selected = opts?.receipt ?? state.selectedReceipt;
    if (!selected) {
      setState((prev) => ({
        ...prev,
        errors: { ...prev.errors, payment: 'No receipt selected for refund' },
      }));
      return null;
    }

    const token = userSession?.token.accessToken ?? '';
    const branchId = userSession?.branchId || 'branch_qalam_benghazi';
    const cashierId = userSession?.user.id || '';
    const allowOfflineCheckout = canSubmitOfflineSale({
      accessToken: token,
      usesLocalCatalog: offlinePos.usesLocalCatalog,
      sessionMode: userSession?.sessionMode,
      isOnline: offlinePos.isOnline,
    });
    if (!allowOfflineCheckout) {
      setState((prev) => ({
        ...prev,
        errors: { ...prev.errors, payment: 'Not authenticated' },
      }));
      return null;
    }

    const saleVisibleId = selected.saleVisibleId || selected.id;

    setState((prev) => ({
      ...prev,
      isProcessingPayment: true,
      selectedReceipt: selected,
      operationMode: OperationMode.REFUND,
    }));

    try {
      const refundItems = opts?.items ?? selected.items;
      const settled = opts?.settled !== false;
      const result = await offlinePos.submitRefund(token, {
        originalSaleId: saleVisibleId,
        originalReceiptId: selected.receiptNumber,
        cashierId,
        branchId,
        items: refundItems,
        refundAmount: opts?.refundAmount ?? selected.grandTotal,
        collectedAmount: opts?.collectedAmount,
        exchanges: opts?.exchanges,
        settled,
        reason: opts?.reason,
      });

      const prior = [
        {
          items: selected.items.map((item, index) => ({
            productId: item.productId,
            quantity: selected.returnedQuantities?.[index] ?? 0,
          })),
        },
        { items: refundItems },
      ];
      const left = remainingQuantities(selected.items, prior);
      const returnedQuantities = selected.items.map(
        (item, index) => item.quantity - (left[index] ?? 0),
      );
      const updatedReceiptHistory = state.receiptHistory.map((receipt) =>
        receipt.id === selected.id
          ? {
              ...receipt,
              status: settled ? ReceiptStatus.REFUNDED : ReceiptStatus.PARTIAL,
              returnedQuantities,
              doneAt: new Date(),
            }
          : receipt,
      );

      const refundRef =
        result.mode === 'offline'
          ? `RMB-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-LOC`
          : `RMB-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${String(Math.floor(Math.random() * 900) + 100)}`;

      setState((prev) => ({
        ...prev,
        isProcessingPayment: false,
        cart: [],
        discountPercentage: 0,
        receiptHistory: updatedReceiptHistory,
        errors: { quantity: '', payment: '', general: '' },
      }));
      flashNotification(
        result.mode === 'offline'
          ? `Remboursement enregistré hors ligne (${selected.receiptNumber})`
          : `Refund completed (${refundRef})`,
        'success',
      );

      return { refundRef };
    } catch (error) {
      setState((prev) => ({
        ...prev,
        isProcessingPayment: false,
        errors: {
          ...prev.errors,
          payment:
            error instanceof Error ? error.message : 'Refund failed',
        },
      }));
      return null;
    }
  }, [
    state.selectedReceipt,
    state.receiptHistory,
    userSession,
    offlinePos,
    flashNotification,
  ]);

  const onCashKeypadPress = useCallback((digit: string) => {
    setState(prev => {
      const currentValue = prev.cashKeypadValue;
      let newValue = currentValue + digit;

      // Prevent multiple decimal points
      if (digit === '.' && currentValue.includes('.')) {
        return prev;
      }
      if (currentValue === '' && digit === '.') {
        newValue = '0.';
      }

      // Limit to reasonable amount
      const numValue = parseFloat(newValue);
      if (numValue > 999999) {
        return prev;
      }

      return {
        ...prev,
        cashKeypadValue: newValue,
        cashGiven: Math.max(0, parseFloat(newValue) || 0),
      };
    });
  }, [])

  const onCashKeypadClear = useCallback(() => {
    setState(prev => ({
      ...prev,
      cashKeypadValue: "",
      cashGiven: 0,
    }));
  }, [])

  const onCashKeypadBackspace = useCallback(() => {
    setState(prev => {
      const next = prev.cashKeypadValue.slice(0, -1);
      return {
        ...prev,
        cashKeypadValue: next,
        cashGiven: Math.max(0, parseFloat(next) || 0),
      };
    });
  }, [])

  const onToggleCashEdit = useCallback(() => {
    setState(prev => ({
      ...prev,
      isEditingCashGiven: !prev.isEditingCashGiven,
      cashKeypadValue: !prev.isEditingCashGiven ? prev.cashGiven.toString() : ""
    }));
  }, [])

  const onShowCashKeypad = useCallback(() => {
    setState(prev => ({
      ...prev,
      showCashKeypad: true,
      cashKeypadValue: ""
    }));
  }, [])

  const onHideCashKeypad = useCallback(() => {
    setState(prev => ({ ...prev, showCashKeypad: false }));
  }, [])

  const onTogglePrintReceipt = useCallback(() => {
    setState(prev => ({
      ...prev,
      shouldPrintReceipt: !prev.shouldPrintReceipt
    }));
  }, [])

  const onCreateNewReceipt = useCallback(() => {
    const newReceipt: Receipt = {
      id: `receipt-${Date.now()}`,
      receiptNumber: generateReceiptNumber(),
      items: [],
      subtotal: 0,
      discountPercentage: 0,
      discountAmount: 0,
      grandTotal: 0,
      paymentMethod: PaymentMethod.CASH,
      cashGiven: 0,
      cashReturned: 0,
      cashierName: state.cashierName,
      createdAt: new Date(),
      doneAt: null,
      status: ReceiptStatus.ONGOING
    };

    setState(prev => ({
      ...prev,
      currentReceipt: newReceipt,
      receiptNumber: newReceipt.receiptNumber
    }));
  }, [state.cashierName])

  const onUpdateReceiptStatus = useCallback((status: ReceiptStatus) => {
    setState(prev => {
      if (!prev.currentReceipt) return prev;

      return {
        ...prev,
        currentReceipt: {
          ...prev.currentReceipt,
          status,
          doneAt: status === ReceiptStatus.COMPLETED || status === ReceiptStatus.CANCELLED ? new Date() : null
        }
      };
    });
  }, [])

  const onSaveReceipt = useCallback(() => {
    setState(prev => {
      if (!prev.currentReceipt) return prev;

      return {
        ...prev,
        receiptHistory: [...prev.receiptHistory, prev.currentReceipt],
        currentReceipt: null
      };
    });
  }, [])

  const onUpdateProductStock = useCallback((productId: string, quantityChange: number) => {
    setState(prev => {
      const updatedProducts = prev.products.map(product =>
        product.id === productId ? { ...product, stock: product.branchStock?.availableStock + quantityChange } : product
      );
      return { ...prev, products: updatedProducts };
    });
  }, [])


  // Reload categories only when branch or catalog revision changes — not when
  // OfflinePos context identity churns (isSyncing / pendingCount / isOnline).
  const onLoadCategoriesRef = useRef(onLoadCategories);
  onLoadCategoriesRef.current = onLoadCategories;
  const lastCatalogUiLoadKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const key = catalogUiLoadKey({
      branchId: userSession?.branchId || 'branch_qalam_benghazi',
      usesLocalCatalog: offlinePos.usesLocalCatalog,
      catalogReady: offlinePos.catalogReady,
      catalogRevision: offlinePos.catalogRevision,
    });
    if (key == null || lastCatalogUiLoadKeyRef.current === key) return;
    lastCatalogUiLoadKeyRef.current = key;
    void onLoadCategoriesRef.current();
  }, [
    userSession?.branchId,
    offlinePos.usesLocalCatalog,
    offlinePos.catalogReady,
    offlinePos.catalogRevision,
  ]);

  return {
    state, intents: {
      onTogglePrintReceipt,
      onToggleCashEdit,
      onCashKeypadPress,
      onCashKeypadClear,
      onCashKeypadBackspace,
      onShowCashKeypad,
      onHideCashKeypad,
      onCreateNewReceipt,
      onUpdateReceiptStatus,
      onSaveReceipt,
      onProductAddToCart,
      onFreePriceProductAddToCart,
      onClearCart,
      onUpdateProductStock,
      onProcessRefund,
      onProductSearch,
      onBarcodeScanSubmit,
      onModeToggle,
      onEnterRefundMode,
      onEditCartItem,
      onUpdateCartQuantity,
      onUpdateCartItemDiscount,
      onCancelCartEdit,
      onQuantityKeypad,
      onRemoveFromCart,
      onDiscountChanged,
      onPaymentMethodSelected,
      onCashGivenChanged,
      onConfirmPurchase,
      onCancelPurchase,
      onLogout,
      onReceiptPrint,
      onReceiptSelected,
      onClearRefund,
      onLookupReceipt,
      onLoadReceiptHistory: loadReceiptHistory,
      onLoadAllProducts,
      onCategorySelected
    }
  };
}; 